import { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import { QueueAction } from '@rossum/api-client/schemas';
import { IconBolt } from '@rossum/ui/icons/tabler';
import { Autocomplete, TextField } from '@rossum/ui/material';
import { getUnsafe } from '../../../lib/helpers';
import { getSchemaFields } from '../../formulas/FormulaEditor/helpers';
import { createForm } from './createForm';
import { createHeader } from './createHeader';

const addValidationSourceActionPayloadSchema = (intl?: IntlShape) =>
  z.object({
    schema_id: z.string().optional(),
    schema_ids: z
      .array(z.string())
      .optional()
      .refine(
        val => val === undefined || val.length > 0,
        intl?.formatMessage({
          id: 'features.queueSettings.rules.actions.addValidationSource.form.schemaId.required',
        })
      ),
  });

const getSelectedSchemaFields = <T extends { id: string }>(
  value: z.infer<ReturnType<typeof addValidationSourceActionPayloadSchema>>,
  schemaFieldOptions: T[]
): T[] => {
  const schemaIds = value.schema_ids;
  if (schemaIds?.length) {
    return schemaFieldOptions.filter(f => schemaIds.includes(f.id));
  }

  if (value.schema_id) {
    const field = schemaFieldOptions.find(f => f.id === value.schema_id);
    return field ? [field] : [];
  }

  return [];
};

const AddValidationSourceActionForm = createForm(
  ({ value, onChange, schemaContent, errors }) => {
    const intl = useIntl();
    const schemaFieldOptions = useMemo(
      () =>
        getSchemaFields(schemaContent).filter(
          field => field.category === 'datapoint'
        ),
      [schemaContent]
    );
    const schemaFields = useMemo(
      () => getSelectedSchemaFields(value, schemaFieldOptions),
      [schemaFieldOptions, value]
    );

    return (
      <Autocomplete
        multiple
        size="small"
        options={schemaFieldOptions}
        getOptionLabel={option => `${option.label} (${option.id})`}
        value={schemaFields}
        onChange={(_e, vs) => {
          onChange({
            ...value,
            schema_ids: vs.map(v => v.id),
            schema_id: undefined,
          });
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={intl.formatMessage({
              id: 'features.queueSettings.rules.actions.addValidationSource.form.fields',
            })}
            placeholder={intl.formatMessage({
              id: 'features.queueSettings.rules.actions.addValidationSource.form.chooseFields',
            })}
            InputLabelProps={{ shrink: true }}
            error={getUnsafe(errors, 'schema_ids') !== undefined}
            helperText={getUnsafe(errors, 'schema_ids.message')?.toString()}
          />
        )}
      />
    );
  },
  intl => addValidationSourceActionPayloadSchema(intl)
);

const MAX_ENTRIES = 3;

const AddValidationSourceActionHeader = createHeader(
  ({ value, schemaContent, RenderComponent }) => {
    const intl = useIntl();
    const schemaFieldOptions = useMemo(
      () => getSchemaFields(schemaContent),
      [schemaContent]
    );
    const schemaFields = useMemo(
      () => getSelectedSchemaFields(value, schemaFieldOptions),
      [schemaFieldOptions, value]
    );
    return (
      <RenderComponent
        entries={[
          ...schemaFields.slice(0, MAX_ENTRIES).flatMap(schemaField => [
            {
              key: `schema_field_${schemaField.id}`,
              label: intl.formatMessage({
                id: 'features.rules.testSection.displayKey.schemaFields',
              }),
              value: schemaField.label,
            },
          ]),
          ...(schemaFields.length > MAX_ENTRIES
            ? [
                {
                  key: 'more_schema_fields',
                  value: `+${schemaFields.length - MAX_ENTRIES}`,
                },
              ]
            : []),
        ]}
      />
    );
  },
  intl => addValidationSourceActionPayloadSchema(intl)
);

export const addValidationSourceAction = {
  icon: IconBolt,
  intlKey: 'addValidationSourceAction' as const,
  resolveAction: (a: QueueAction) => a.type === 'add_validation_source',
  serialized: { type: 'add_validation_source' as const },
  groupIntlKey: 'automation' as const,
  form: AddValidationSourceActionForm,
  header: AddValidationSourceActionHeader,
  supportedEvents: ['validation'] as const,
  payloadSchema: (intl?: IntlShape) =>
    addValidationSourceActionPayloadSchema(intl),
};
